// src/components/common/ImageUpload.js
import React, { useState, useRef } from 'react';
import { uploadFile, resolveIPFS, testPinataAuth } from '../../services/ipfs';

// Validate file before upload (kept local — UI-specific)
const validateFile = (file, options = {}) => {
  const maxSize = options.maxSize || 10 * 1024 * 1024;
  const allowedTypes = options.allowedTypes || ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  if (!file) return { valid: false, error: 'No file provided' };
  if (file.size > maxSize) return { valid: false, error: `File size exceeds ${(maxSize / (1024 * 1024)).toFixed(1)}MB limit` };
  if (!allowedTypes.includes(file.type)) return { valid: false, error: `File type ${file.type} is not allowed` };
  return { valid: true };
};
import './ImageUpload.css';

const ImageUpload = ({
  value,
  onChange,
  label = 'Image',
  required = false,
  maxSize = 10 * 1024 * 1024, // 10MB
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  placeholder = 'Upload image or enter URL',
  helpText = '',
  className = '',
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [inputMode, setInputMode] = useState(value?.startsWith('http') || value?.startsWith('ipfs://') ? 'url' : 'upload');
  const fileInputRef = useRef(null);

  const [pinataConfigured, setPinataConfigured] = useState(true);
  // Check Pinata auth on mount
  React.useEffect(() => {
    testPinataAuth().then(r => setPinataConfigured(r.ok));
  }, []);

  const handleFileSelect = async (file) => {
    if (!file) return;

    setError(null);

    // Validate file
    const validation = validateFile(file, { maxSize, allowedTypes });
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    if (!pinataConfigured) {
      setError('Pinata is not configured. Please set REACT_APP_PINATA_JWT in your environment.');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const result = await uploadFile(file, `event-image-${Date.now()}`);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Use gateway URL for display, store IPFS URL
      const gatewayUrl = result.url;
      const ipfsUrl = `ipfs://${result.ipfsHash}`;
      onChange(gatewayUrl, {
        ipfsHash: result.ipfsHash,
        ipfsUrl,
        gatewayUrl,
      });

      setTimeout(() => setUploadProgress(0), 500);
    } catch (err) {
      console.error('[ImageUpload] Upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    if (disabled || uploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    onChange('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getDisplayUrl = () => {
    if (!value) return '';
    return resolveIPFS(value);
  };

  return (
    <div className={`image-upload-container ${className}`}>
      {label && (
        <label className="image-upload-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}

      <div className="image-upload-tabs">
        <button
          type="button"
          className={`tab-btn ${inputMode === 'upload' ? 'active' : ''}`}
          onClick={() => setInputMode('upload')}
          disabled={disabled}
        >
          Upload File
        </button>
        <button
          type="button"
          className={`tab-btn ${inputMode === 'url' ? 'active' : ''}`}
          onClick={() => setInputMode('url')}
          disabled={disabled}
        >
          Enter URL
        </button>
      </div>

      {inputMode === 'upload' ? (
        <div
          className={`image-upload-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''} ${disabled ? 'disabled' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={!uploading && !disabled ? handleBrowseClick : undefined}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedTypes.join(',')}
            onChange={handleInputChange}
            disabled={disabled || uploading}
            style={{ display: 'none' }}
          />

          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p>Uploading to IPFS... {uploadProgress}%</p>
            </div>
          ) : value ? (
            <div className="upload-preview">
              <img
                src={getDisplayUrl()}
                alt="Preview"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="preview-overlay">
                <button
                  type="button"
                  className="btn-change"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowseClick();
                  }}
                >
                  Change
                </button>
                <button
                  type="button"
                  className="btn-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-placeholder">
              <div className="upload-icon">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
              </div>
              <p className="upload-text">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="upload-hint">
                {allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')} up to {(maxSize / (1024 * 1024)).toFixed(0)}MB
              </p>
              {!pinataConfigured && (
                <p className="config-warning">
                  Pinata not configured - upload disabled
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="image-upload-url">
          <input
            type="url"
            value={value || ''}
            onChange={handleUrlChange}
            placeholder={placeholder}
            disabled={disabled}
            className="url-input"
          />
          {value && (
            <button
              type="button"
              className="btn-clear-url"
              onClick={handleClear}
              disabled={disabled}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {error && <p className="image-upload-error">{error}</p>}

      {helpText && <p className="image-upload-help">{helpText}</p>}

      {/* Preview for URL mode */}
      {inputMode === 'url' && value && (
        <div className="url-preview">
          <img
            src={getDisplayUrl()}
            alt="Preview"
            onError={(e) => {
              e.target.parentElement.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Show IPFS info if available */}
      {value && value.includes('ipfs') && (
        <p className="ipfs-info">
          Stored on IPFS
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
